/**
 * tempo-straighten.js — window.TempoStraighten / module.exports
 *
 * ПЛАВАЮЩИЙ ТЕМП → РОВНЫЙ: каждая доля записи встаёт на ровную сетку заданного
 * BPM, тональность не меняется.
 *
 * Зачем. /time-stretch умел один коэффициент на весь файл: «исходный BPM →
 * целевой». Для записи, у которой темп гуляет, это ничего не решает — дрейф
 * растягивается вместе с треком. Жалоба 24.09.2026 (Премиум, купленный прямо со
 * страницы тула): «как плавал темп так и плавает, ничего не меняется» — человек
 * поставил целевой темп равным найденному (134 → 134) и получил тот же файл.
 * Такие выдачи — «исходный = целевой, тон не тронут» — 150 за 90 дней в RU-файле
 * аналитики, 98 сессий.
 *
 * Насколько это частый случай, замерено на наших же образцах стилей, которые
 * сгенерировала нейросеть (public/audio/ai-styles, 34 трека): у половины доли
 * уходят от лучшей ровной сетки больше чем на 100 мс, у edm-club / disco /
 * dance-pop / metal — на 0.5–0.8 с (больше целой доли), темп гуляет в пределах
 * ±1.5–2 %. Дрейф подтверждён НЕЗАВИСИМЫМ методом (фаза огибающей онсетов
 * относительно ровной сетки, без трекера долей): расхождение с трекером — 9–15 мс
 * по медиане. Suno сам пишет об этом в справке («Fixing Tempo Drift»).
 *
 * Как устроено (каждый выбор — по стенду с известной истиной,
 * tests/scratch → памятка tool_time_stretch):
 *
 *   1. Доли — BeatGrid.track (DP Эллиса по той же огибающей, что у детектора
 *      темпа). Двухпроходный DP с локальным периодом и жёстким штрафом
 *      проверен и ОТВЕРГНУТ: на синтетике он чинил одно скольжение фазы, а на
 *      настоящих edm-club и metal сам уезжал (p90 24 → 350 мс, 32 → 346 мс).
 *   2. Переиндексация: пропущенная доля (интервал ≈ 2T) не сдвигает номер всех
 *      следующих, двойная — выбрасывается.
 *   3. Вес доли = опора онсетов под ней (поток в доле против локальной медианы).
 *      Доли, которые трекер поставил «по инерции» через вступление без ударных,
 *      почти не весят и не тянут кривую.
 *   4. Взвешенная локальная линейная регрессия по ±4 долям: убирает шум
 *      трекера (±10 мс), не трогая медленный дрейф. Стенд (16 случаев, известная
 *      истина): остаток p95 12 мс против 377 у равномерного растяжения,
 *      дрожание локального коэффициента 0.38 % (каждая доля без сглаживания —
 *      0.98 %: детектор шумит, и этот шум стал бы колебанием темпа).
 *   5. Кривая натяжения: доля k → base + k·T. Голова (звук до первой доли) идёт
 *      коэффициентом первого участка — тогда она помещается ВСЕГДА, в том числе
 *      при одновременной смене темпа больше чем на 10 % (warp-map отверг бы).
 *
 * Вердикт «выровняю» (verdict 'drifting') выдаётся, только когда за него
 * можно поручиться, и проверен на 76 случаях с ИЗВЕСТНОЙ истиной (ровные
 * ИИ-треки, раскачанные известной кривой ±1.5 % и ±3 %; 48 случаев подбора
 * порогов + 28 вне выборки — другие треки и сиды): 57 верных обещаний, 0 ложных,
 * все 15 провалов трекера получили «не уверен», 4 удачных — осторожное «не
 * уверен». Три условия:
 *   • темп меняется плавно — скачок локального темпа за 8 долей ≤ 4 %
 *     (соскальзывание трекера на полдоли или лишняя доля в тихой секции дают
 *     4.1–8.2 %; настоящий дрейф — 0.9–3.5 %);
 *   • пульс слышен почти везде — доля долей без опоры онсетов < 25 %;
 *   • фазовая мера (огибающая ВХОДА, перенесённая через кривую, против
 *     целевой сетки; без рендера, миллисекунды) ≤ 70 мс и минимум на 30 %
 *     лучше ровной сетки. Одна фазовая мера судьёй быть не может: её
 *     обманывает то же, что трекер (где слабая доля громче сильной, обе
 *     принимают её за долю) — на 6 провалах она показала 23–47 мс.
 * На 34 настоящих песнях нейросети: 10 «выровняю» (edm-club 537 → 28 мс,
 * dance-pop 420 → 17, disco 464 → 45, metal 470 → 35), 15 «ровный», 9 «не
 * уверен» — свинг (blues, jazz), 3/4, неверный метр (rock-ballad, punk),
 * слабый пульс. После рендера обещание проверяется на самом выходе (verify).
 *
 * ⚠️ Чистая математика: только window.__onsetEnvelope и window.BeatGrid
 * (bpm-detector.js, beat-grid.js), без DOM и Web Audio — те же числа
 * считаются в Node (tests/js/tempo-straighten.test.mjs).
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory;
    else root.TempoStraighten = factory(root);
})(typeof self !== 'undefined' ? self : this, function (root) {
    'use strict';

    /* ±4 доли — окно сглаживания (стенд: 2/4/8/16; 4 даёт меньший остаток при
       том же дрожании, 16 уже не успевает за дрейфом). */
    var SMOOTH_BEATS = 4;
    /* Опора онсетов → вес: ниже 1.3× локальной медианы потока доля не слышна,
       выше 3× — слышна наверняка. */
    var SUPPORT_LO = 1.3, SUPPORT_HI = 3;
    /* Отклонение (p90, мс) лучшей РОВНОЙ сетки от долей записи, ниже которого
       выравнивать нечего: это уровень шума самой меры на ровных треках. */
    var STEADY_MS = 35;
    /* Насколько выравнивание обязано улучшить запись по той же фазовой мере,
       чтобы тул за него поручился, и какой остаток меры ещё терпим. 70 мс —
       по стенду с известной истиной: у регги и песен со слабой долей шум самой
       меры на удачных выравниваниях 63–71 мс, а drum-and-bass с реальным
       остатком 91–112 мс показывал 77–80 — при пороге 80 это были ложные
       обещания, при 60 — зря забракованные регги. */
    var FIX_RATIO = 0.7;
    var JUDGE_MS = 70;
    /* Соскальзывание трекера видно по ФОРМЕ, а не фазовой мерой (её
       обманывает то же, что трекер: где слабая доля громче сильной, обе
       принимают её за долю). Настоящий дрейф плавный: за 8 долей темп
       меняется на 0.9–3.5 % (48 случаев с известной истиной); соскальзывание
       на полдоли или лишняя доля в тихой секции — это 4.1–8.2 %. Плюс доля
       долей без опоры онсетов: где пульса почти не слышно (оркестр, брейки),
       обещать нечего. */
    var MAX_JUMP_PCT = 4;
    var MAX_WEAK = 0.25;
    /* Поправка темпа на участке, больше которой это уже не дрейф, а ошибка
       сетки (или смена темпа, которую ровной сеткой не описать). */
    var MAX_CORRECTION = 1.18;

    function isNum(v) { return typeof v === 'number' && isFinite(v); }
    function median(a) { var s = a.slice().sort(function (x, y) { return x - y; }); return s[s.length >> 1]; }

    function env(ab) {
        var f = root.__onsetEnvelope;
        return typeof f === 'function' ? f(ab) : null;
    }

    /* Номера долей: пропуск (интервал ≈ 2T) оставляет дырку в номерах, а не
       сдвигает все следующие; сдвоенная доля выбрасывается. */
    function reindex(beats) {
        var out = [{ k: 0, t: beats[0] }], k = 0;
        for (var i = 1; i < beats.length; i++) {
            var lo = Math.max(1, i - 8), hi = Math.min(beats.length - 1, i + 8), ib = [];
            for (var j = lo; j <= hi; j++) ib.push(beats[j] - beats[j - 1]);
            var T = median(ib);
            var steps = Math.round((beats[i] - out[out.length - 1].t) / T);
            if (steps < 1) continue;
            k += steps;
            out.push({ k: k, t: beats[i] });
        }
        return out;
    }

    /* Опора онсетов под каждой долей, 0..1. */
    function supportWeights(e, pts) {
        var flux = e.flux, n = flux.length, fps = e.dsr / e.hop, half = (e.fftN / 2) / e.dsr;
        var R = Math.round(4 * fps);
        return pts.map(function (p) {
            var f = Math.round((p.t - half) * fps);
            if (f < 1 || f >= n - 1) return 0;
            var s = Math.max(flux[f - 1], flux[f], flux[f + 1]);
            var w = [];
            for (var j = Math.max(0, f - R); j < Math.min(n, f + R); j += 2) w.push(flux[j]);
            var ref = median(w) || 1e-9;
            var u = Math.max(0, Math.min(1, (s / ref - SUPPORT_LO) / (SUPPORT_HI - SUPPORT_LO)));
            return u * u * (3 - 2 * u);
        });
    }

    /* Взвешенная локальная линейная регрессия t(k) по ±w долям (трикуб × вес).
       Где опоры мало, окно растёт — тишина продолжает темп соседей. */
    function smooth(pts, w, weights) {
        var n = pts.length, out = new Array(n);
        for (var i = 0; i < n; i++) {
            var ww = w, done = false;
            while (!done) {
                var sw = 0, sk = 0, st = 0, skk = 0, skt = 0;
                for (var j = 0; j < n; j++) {
                    var dk = pts[j].k - pts[i].k;
                    if (Math.abs(dk) > ww) continue;
                    var u = Math.abs(dk) / (ww + 1);
                    var q = Math.pow(1 - u * u * u, 3) * weights[j];
                    sw += q; sk += q * dk; st += q * pts[j].t; skk += q * dk * dk; skt += q * dk * pts[j].t;
                }
                var den = sw * skk - sk * sk;
                if (sw >= 1.5 && Math.abs(den) > 1e-9) {
                    var b = (sw * skt - sk * st) / den;
                    out[i] = { k: pts[i].k, t: (st - b * sk) / sw, w: weights[i] };
                    done = true;
                } else if (ww > n) {
                    out[i] = { k: pts[i].k, t: pts[i].t, w: weights[i] };
                    done = true;
                } else ww *= 2;
            }
        }
        return out;
    }

    function fitPeriod(pts) {
        var n = pts.length, sk = 0, st = 0, skk = 0, skt = 0;
        for (var i = 0; i < n; i++) { sk += pts[i].k; st += pts[i].t; skk += pts[i].k * pts[i].k; skt += pts[i].k * pts[i].t; }
        return (n * skt - sk * st) / (n * skk - sk * sk);
    }

    /* Якоря → пары «вход → выход». Только внутри файла, строго по возрастанию;
       голова идёт коэффициентом первого участка (помещается всегда). */
    function pairsFor(anchors, duration, T) {
        var pts = [];
        for (var i = 0; i < anchors.length; i++) {
            var p = anchors[i];
            if (!(p.t > 0.001) || !(p.t < duration - 0.001)) continue;
            var last = pts[pts.length - 1];
            if (last && (p.t <= last.t + 0.01 || p.k <= last.k)) continue;
            pts.push(p);
        }
        if (pts.length < 2) return null;
        var Tfirst = (pts[1].t - pts[0].t) / (pts[1].k - pts[0].k);
        var base = pts[0].t * T / Tfirst;
        return pts.map(function (q) { return [q.t, base + (q.k - pts[0].k) * T]; });
    }

    function mapperOf(pairs) {
        var xs = pairs.map(function (p) { return p[0]; }), ys = pairs.map(function (p) { return p[1]; });
        var n = xs.length;
        var s0 = (ys[1] - ys[0]) / (xs[1] - xs[0]), sN = (ys[n - 1] - ys[n - 2]) / (xs[n - 1] - xs[n - 2]);
        return function (x) {
            if (x <= xs[0]) return ys[0] + (x - xs[0]) * s0;
            if (x >= xs[n - 1]) return ys[n - 1] + (x - xs[n - 1]) * sN;
            var lo = 0, hi = n - 1;
            while (hi - lo > 1) { var m = (lo + hi) >> 1; if (xs[m] <= x) lo = m; else hi = m; }
            return ys[lo] + (x - xs[lo]) * (ys[hi] - ys[lo]) / (xs[hi] - xs[lo]);
        };
    }

    /**
     * Насколько ровно онсеты стоят на сетке периода T с началом a, если
     * огибающую провести через отображение времени map (null — как есть).
     * Окна 8 с через 2 с: фаза огибающей на периоде T в каждом окне и её
     * КРУГОВОЕ отклонение от среднего. Фазу НЕ разворачивают: в брейкдауне без
     * ударных она случайна, развёртка ошибается на целый период, и всё после
     * брейкдауна «съезжает» на долю — первая версия так показала 385 мс у
     * трека, который на деле стоял в 24 мс. Окна взвешены «захватом» (там, где
     * пульса нет, фаза не голосует). → { p90, max } в мс; предел — полдоли.
     */
    function steadiness(e, map, T, a) {
        var fps = e.dsr / e.hop, half = (e.fftN / 2) / e.dsr, flux = e.flux, n = flux.length;
        var W = 8, HOP = 2;
        var outT = new Float64Array(n);
        for (var f = 0; f < n; f++) { var t = f / fps + half; outT[f] = map ? map(t) : t; }
        var dur = outT[n - 1], wins = [], f0 = 0;
        for (var c = W / 2; c + W / 2 <= dur; c += HOP) {
            var re = 0, im = 0, tot = 0;
            while (f0 < n && outT[f0] < c - W / 2) f0++;
            for (var g = f0; g < n && outT[g] < c + W / 2; g++) {
                var hw = 0.5 + 0.5 * Math.cos(Math.PI * (outT[g] - c) / (W / 2));
                var ph0 = 2 * Math.PI * (outT[g] - a) / T;
                re += hw * flux[g] * Math.cos(ph0); im += hw * flux[g] * Math.sin(ph0); tot += hw * flux[g];
            }
            if (tot > 0) wins.push({ ph: Math.atan2(im, re), lock: Math.hypot(re, im) / tot });
        }
        return circularSpread(wins, T);
    }

    /* Круговое среднее фаз окон (веса — захват) и отклонения от него в мс. */
    function circularSpread(wins, T) {
        if (wins.length < 2) return { p90: null, max: null, n: wins.length };
        var cx = 0, cy = 0, sw = 0, i;
        for (i = 0; i < wins.length; i++) { cx += wins[i].lock * Math.cos(wins[i].ph); cy += wins[i].lock * Math.sin(wins[i].ph); sw += wins[i].lock; }
        var mean = Math.atan2(cy, cx), avg = sw / wins.length;
        var devs = wins.map(function (o) {
            var d = o.ph - mean;
            while (d > Math.PI) d -= 2 * Math.PI;
            while (d < -Math.PI) d += 2 * Math.PI;
            return { d: Math.abs(d) / (2 * Math.PI) * T * 1000, w: o.lock };
        }).sort(function (x, y) { return x.d - y.d; });
        var acc = 0, p90 = devs[devs.length - 1].d, max = 0;
        for (i = 0; i < devs.length; i++) { acc += devs[i].w; if (acc >= 0.9 * sw) { p90 = devs[i].d; break; } }
        for (i = 0; i < devs.length; i++) if (devs[i].w >= 0.5 * avg && devs[i].d > max) max = devs[i].d;
        return { p90: Math.round(p90), max: Math.round(max), n: wins.length };
    }

    /* Дрейф по самим долям: насколько сглаженные якоря уходят от лучшей
       РОВНОЙ сетки (взвешенная линейная аппроксимация номер → время). Это «до»
       — ровно то, что оставлял прежний тул с одним коэффициентом. Независимость
       здесь не нужна: мера лишь говорит, есть ли что чинить, а обещание
       проверяет steadiness. */
    function driftOf(anchors) {
        var sw = 0, sk = 0, st = 0, skk = 0, skt = 0, i, p, q;
        for (i = 0; i < anchors.length; i++) {
            p = anchors[i]; q = Math.max(0.05, p.w);
            sw += q; sk += q * p.k; st += q * p.t; skk += q * p.k * p.k; skt += q * p.k * p.t;
        }
        var b = (sw * skt - sk * st) / (sw * skk - sk * sk), a0 = (st - b * sk) / sw;
        var devs = anchors.map(function (x) { return { d: Math.abs(x.t - (a0 + b * x.k)) * 1000, w: Math.max(0.05, x.w) }; })
            .sort(function (x, y) { return x.d - y.d; });
        var acc = 0, p90 = devs[devs.length - 1].d, max = 0;
        for (i = 0; i < devs.length; i++) { acc += devs[i].w; if (acc >= 0.9 * sw) { p90 = devs[i].d; break; } }
        for (i = 0; i < devs.length; i++) if (devs[i].w >= 0.5 && devs[i].d > max) max = devs[i].d;
        return { p90: Math.round(p90), max: Math.round(max), T: b };
    }

    /* Наибольшее изменение локального темпа (по 4 долям) на расстоянии 8
       долей, в процентах — подпись соскальзывания трекера (см. MAX_JUMP_PCT). */
    function tempoJump(anchors) {
        var loc = [], i;
        for (i = 4; i < anchors.length; i++) {
            var per = (anchors[i].t - anchors[i - 4].t) / (anchors[i].k - anchors[i - 4].k);
            if (per > 0) loc.push(1 / per);
        }
        var mx = 0;
        for (i = 8; i < loc.length; i++) mx = Math.max(mx, Math.abs(loc[i] / loc[i - 8] - 1));
        return Math.round(mx * 1000) / 10;
    }

    /* Локальный темп по сглаженным якорям: для подписи «плавает 125–129» и
       кривой под волной. */
    function tempoCurve(anchors) {
        var pts = [], i;
        for (i = 4; i < anchors.length; i += 2) {
            var a = anchors[i - 4], b = anchors[i];
            var per = (b.t - a.t) / (b.k - a.k);
            if (per > 0) pts.push({ t: (a.t + b.t) / 2, bpm: 60 / per });
        }
        if (!pts.length) return { points: [], lo: null, hi: null };
        var s = pts.map(function (p) { return p.bpm; }).sort(function (x, y) { return x - y; });
        return {
            points: pts,
            lo: s[Math.round(0.05 * (s.length - 1))],
            hi: s[Math.round(0.95 * (s.length - 1))]
        };
    }

    /**
     * Анализ записи. opts.bpm — темп, который человек поправил руками (×2, ÷2,
     * тап): сетка строится на нём, уровень не угадывается.
     *
     * → { ok, reason?, verdict: 'steady'|'drifting'|'unsure', bpm, beats,
     *     anchors, curve, lo, hi, before, after, weak, ms }
     */
    function analyze(ab, opts) {
        var o = opts || {}, t0 = Date.now();
        var e = env(ab);
        if (!e) return { ok: false, reason: 'too_short' };
        if (!root.BeatGrid || typeof root.BeatGrid.track !== 'function') return { ok: false, reason: 'no_beat_grid' };
        var g = root.BeatGrid.track(ab, o.bpm > 0 ? { bpm: o.bpm } : undefined);
        if (!g || !g.beats || g.beats.length < 16) return { ok: false, reason: 'no_beats' };

        var pts = reindex(g.beats);
        var w = supportWeights(e, pts);
        var anchors = smooth(pts, SMOOTH_BEATS, w);
        var before = driftOf(anchors);
        var T = before.T;
        if (!(T > 0)) return { ok: false, reason: 'no_beats' };

        var selfPairs = pairsFor(anchors, ab.duration, T);
        if (!selfPairs) return { ok: false, reason: 'no_beats' };
        var after = steadiness(e, mapperOf(selfPairs), T, selfPairs[0][1]);
        /* Та же мера на НЕвыровненной записи при ровной сетке того же периода:
           сравнивать «после» можно только с этим, а не с дрейфом по долям —
           у мер разный уровень шума. */
        var beforePhase = steadiness(e, null, T, 0);
        var weak = 0;
        for (var i = 0; i < w.length; i++) if (w[i] < 0.2) weak++;
        weak = weak / w.length;
        var jump = tempoJump(anchors);
        var curve = tempoCurve(anchors);

        /* Вердикт. «Ровный» — доли и так на ровной сетке. «Плавает» — тул
           ручается за выравнивание: темп меняется плавно (нет скачков —
           подписи соскальзывания трекера), пульс слышен почти везде, а фазовая
           мера после кривой и терпима, и заметно лучше ровной сетки. Всё
           остальное — «не уверен»: запись плывёт, но доли местами
           неоднозначны (свинг, 3/4, брейки), и человек решает сам, послушав. */
        var verdict;
        if (before.p90 <= STEADY_MS) verdict = 'steady';
        else if (jump <= MAX_JUMP_PCT && weak < MAX_WEAK
            && after.p90 != null && after.p90 <= JUDGE_MS
            && (beforePhase.p90 == null || after.p90 <= beforePhase.p90 * FIX_RATIO)) verdict = 'drifting';
        else verdict = 'unsure';

        return {
            ok: true,
            verdict: verdict,
            bpm: 60 / T,
            beats: g.beats,
            anchors: anchors,
            curve: curve.points,
            lo: curve.lo,
            hi: curve.hi,
            before: { p90: before.p90, max: before.max, phase: beforePhase.p90 },
            after: { p90: after.p90, max: after.max },
            weak: Math.round(weak * 100) / 100,
            jump: jump,
            duration: ab.duration,
            ms: Date.now() - t0
        };
    }

    /**
     * План натяжения на целевой темп. → { pairs, T, rateMin, rateMax } или
     * { error } с причиной (вызывающий показывает её, а не тянет наугад).
     */
    function plan(analysis, opts) {
        if (!analysis || !analysis.ok) return { error: 'no_analysis' };
        var bpm = opts && opts.bpm > 0 ? opts.bpm : analysis.bpm;
        var T = 60 / bpm;
        var pairs = pairsFor(analysis.anchors, analysis.duration, T);
        if (!pairs) return { error: 'no_anchors' };
        var global = (60 / analysis.bpm) / T; // вход на выход в среднем
        var rmin = Infinity, rmax = 0;
        for (var i = 1; i < pairs.length; i++) {
            var r = (pairs[i][0] - pairs[i - 1][0]) / (pairs[i][1] - pairs[i - 1][1]);
            if (r < rmin) rmin = r;
            if (r > rmax) rmax = r;
        }
        if (rmax / global > MAX_CORRECTION || global / rmin > MAX_CORRECTION) {
            return { error: 'correction_too_large', rateMin: rmin, rateMax: rmax };
        }
        return { pairs: pairs, T: T, bpm: bpm, rateMin: rmin, rateMax: rmax, anchors: pairs.length };
    }

    /**
     * Проверка ВЫДАННОГО звука: те же окна фазы, но по огибающей самого
     * выхода. Считается кусками по 10 с (шесть окон по всей длине) — огибающая
     * целой песни держала бы главный поток секунды. → Promise<{ p90, span, n }>
     */
    function verify(out, planned, opts) {
        var o = opts || {}, N = o.windows || 6, SEC = o.seconds || 10;
        var first = planned.pairs[0][1], last = planned.pairs[planned.pairs.length - 1][1];
        var sr = out.sampleRate, T = planned.T;
        var starts = [];
        for (var i = 0; i < N; i++) {
            var s = first + (last - first - SEC) * (N === 1 ? 0 : i / (N - 1));
            if (s >= 0 && s + SEC <= out.duration) starts.push(s);
        }
        var wins = [];
        function view(s) {
            var a = Math.floor(s * sr), b = Math.min(out.length, a + Math.floor(SEC * sr));
            var chans = [];
            for (var c = 0; c < out.numberOfChannels; c++) chans.push(out.getChannelData(c).subarray(a, b));
            return { sampleRate: sr, numberOfChannels: chans.length, length: b - a, duration: (b - a) / sr, getChannelData: function (c) { return chans[c]; } };
        }
        return new Promise(function (resolve) {
            var idx = 0;
            (function step() {
                if (idx >= starts.length) { resolve(circularSpread(wins, T)); return; }
                var s = starts[idx++];
                var e = env(view(s));
                if (e) {
                    var fps = e.dsr / e.hop, half = (e.fftN / 2) / e.dsr, re = 0, im = 0, tot = 0;
                    for (var f = 0; f < e.flux.length; f++) {
                        var t = s + f / fps + half;
                        var ph0 = 2 * Math.PI * (t - first) / T;
                        re += e.flux[f] * Math.cos(ph0); im += e.flux[f] * Math.sin(ph0); tot += e.flux[f];
                    }
                    if (tot > 0) wins.push({ ph: Math.atan2(im, re), lock: Math.hypot(re, im) / tot });
                }
                setTimeout(step, 0);
            })();
        });
    }

    return {
        analyze: analyze,
        plan: plan,
        verify: verify,
        // для тестов и стенда
        _reindex: reindex,
        _smooth: smooth,
        _pairsFor: pairsFor,
        _steadiness: steadiness,
        _mapperOf: mapperOf,
        STEADY_MS: STEADY_MS
    };
});
